import { useMemo } from "react";
import React from "react";
import { GoogleMap } from "../GoogleMaps/GoogleMap";
import { MapBoundsFitter } from "../GoogleMaps/components/MapBoundsFitter";
import { AdvancedMarker } from "@vis.gl/react-google-maps";
import StoreIcon from "../Icons/store-icon";
import ShoppingBagIcon from "../Icons/shopping-bag-icon";
import { parseDate, formatDate, DATE_FORMATS } from "../../Util/dateUtils";
import styles from "./orders.module.css";

const StorePin = () => {
  return (
    <div 
      data-size="Large" 
      className={styles.storePin}
    >
      <div className={styles.storePinIcon}>
        <StoreIcon />
      </div>
    </div>
  )
}

const formatTimeOnly = (timestamp) => {
  if (!timestamp) return "—";
  try {
    const date = parseDate(timestamp);
    return formatDate(date, DATE_FORMATS.HHcMM_AP);
  } catch (e) {
    return "—";
  }
};

const CustomerPin = ({ order, onOrderClick }) => {
  const deliveryTime = formatTimeOnly(order?.getExpectedDeliveryTime());
  
  const handleClick = (e) => {
    e.stopPropagation();
    if (onOrderClick && order?.getOrderId() && order?.getCompanyId()) {
      onOrderClick(order.getOrderId(), order.getCompanyId());
    }
  };
  
  return (
    <div className={styles.customerPin} onClick={handleClick}>
      <div data-size="Xsmall" className={styles.customerPinIconContainer}>
        <div className={styles.customerPinIconWrapper}>
          <ShoppingBagIcon />
        </div>
      </div>
      <div className={styles.customerPinTime}>
        {deliveryTime}
      </div>
    </div>
  );
}

const OrdersCarriersMap = ({ ordersData, onOrderClick }) => {
  // Extract unique restaurants (deduplicated by restaurant ID)
  const uniqueRestaurants = useMemo(() => {
    if (!ordersData || !ordersData.getData) {
      return [];
    }

    const orders = ordersData.getData();
    const restaurantMap = new Map();

    orders.forEach(order => {
      const restaurant = order.getRestaurant();
      if (restaurant && restaurant.getId() != null) {
        const restaurantId = restaurant.getId();
        // Only add if we haven't seen this restaurant ID before
        if (!restaurantMap.has(restaurantId) && 
            restaurant.getLatitude() != null && 
            restaurant.getLongitude() != null) {
          restaurantMap.set(restaurantId, restaurant);
        }
      }
    });

    return Array.from(restaurantMap.values());
  }, [ordersData]);

  // Extract all customer locations from orders (no deduplication)
  const customerOrders = useMemo(() => {
    if (!ordersData || !ordersData.getData) {
      return [];
    }

    const orders = ordersData.getData();
    return orders.filter(order => {
      const customer = order.getCustomer();
      return customer && 
             customer.getLatitude() != null && 
             customer.getLongitude() != null;
    });
  }, [ordersData]);

  // Extract all customer and restaurant locations from orders for bounds fitting
  const allLocations = useMemo(() => {
    if (!ordersData || !ordersData.getData) {
      return [];
    }

    const orders = ordersData.getData();
    const locations = [];

    orders.forEach(order => {
      const customer = order.getCustomer();
      if (customer && customer.getLatitude() != null && customer.getLongitude() != null) {
        locations.push({
          latitude: customer.getLatitude(),
          longitude: customer.getLongitude()
        });
      }

      const restaurant = order.getRestaurant();
      if (restaurant && restaurant.getLatitude() != null && restaurant.getLongitude() != null) {
        locations.push({
          latitude: restaurant.getLatitude(),
          longitude: restaurant.getLongitude()
        });
      }
    });

    return locations;
  }, [ordersData]);

  return (
    <div style={{ flex: 1, position: 'relative' }}>
      <GoogleMap center={{ lat: 0, lng: 0 }} zoom={2}>
        <MapBoundsFitter locations={allLocations} />
        {customerOrders.map((order, index) => {
          const customer = order.getCustomer();
          return (
            <AdvancedMarker
              key={`customer-${order.getOrderId()}-${index}`}
              position={{ 
                lat: customer.getLatitude(), 
                lng: customer.getLongitude() 
              }}
              anchorLeft="-50%"
              anchorTop="-50%"
            >
              <CustomerPin order={order} onOrderClick={onOrderClick} />
            </AdvancedMarker>
          );
        })}
        {uniqueRestaurants.map((restaurant) => (
          <AdvancedMarker
            key={`restaurant-${restaurant.getId()}`}
            position={{ 
              lat: restaurant.getLatitude(), 
              lng: restaurant.getLongitude() 
            }}
            anchorLeft="-50%"
            anchorTop="-50%"
          >
            <StorePin />
          </AdvancedMarker>
        ))}
      </GoogleMap>
    </div>
  );
};

export default OrdersCarriersMap;
